import {Alert, Button} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {usePreviewPromotionalPhotoVehicleSelectionMutation} from '@dms/api/sales';
import {getApiErrorMessages} from '@dms/api/utils';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {VEHICLE_SELECTION_PREVIEW_LIMIT} from '../../constants/VEHICLE_SELECTION_PREVIEW_LIMIT';
import {VehicleSelectionDraft} from '../../types/VehicleSelectionDraft';
import {VehicleSelectionPreviewResult} from './components/VehicleSelectionPreviewResult';

interface VehicleSelectionImpactPreviewProps extends RequiredTestIdProps {
  // null for a photo that is not saved yet.
  promotionalPhotoId: string | Nullish;
  // Read on click, so the preview always reflects the current form state. Resolves to null when
  // the draft fails the form's rules, which then show on the fields instead of a preview of a
  // filter that matches every vehicle. An existing photo is previewed once on open.
  getDraft: () => Promise<VehicleSelectionDraft | null>;
  // Fingerprint of the draft inputs. A result belongs to the draft it was computed from, so a
  // changed draft hides it until the next run instead of showing an impact that no longer holds.
  draftKey: string;
}

// Read-only: the BE computes what saving the draft would change, and assigns nothing. A 403
// (price condition without the selling-price permission, no vehicle to read) and a 400 (an
// inconsistent draft) are shown here as a message, not as a form error.
export function VehicleSelectionImpactPreview(props: VehicleSelectionImpactPreviewProps) {
  const [previewVehicleSelection, {data: preview, isLoading, error}] =
    usePreviewPromotionalPhotoVehicleSelectionMutation();
  const [previewedKey, setPreviewedKey] = useState<string | null>(null);

  // The hook keeps the response and the error, so the request itself is not awaited.
  const runPreview = async () => {
    const draft = await props.getDraft();
    if (isNil(draft)) {
      return;
    }
    setPreviewedKey(props.draftKey);
    void previewVehicleSelection({
      previewPromotionalPhotoVehicleSelectionRequestBody: {
        promotionalPhotoId: props.promotionalPhotoId ?? null,
        vehicleSelection: draft.vehicleSelection,
        autoAssign: draft.autoAssign,
        limit: VEHICLE_SELECTION_PREVIEW_LIMIT,
      },
    });
  };

  useOnMount(() => {
    if (isNotNil(props.promotionalPhotoId)) {
      void runPreview();
    }
  });

  const isCurrent = previewedKey === props.draftKey;
  const hasOutdatedResult = !isCurrent && (isNotNil(preview) || isNotNil(error));

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <HStack spacing={4} justify="space-between" align="center">
        <VStack spacing={1}>
          <Heading size={5}>{i18n.t('entity.promoPhotos.preview.labels.title')}</Heading>
          <Text size="xSmall" color="tertiary">
            {i18n.t('entity.promoPhotos.preview.labels.description')}
          </Text>
        </VStack>
        <Button
          variant="outlined"
          leftIcon="action/visibility"
          title={i18n.t('entity.promoPhotos.preview.actions.previewImpact')}
          isLoading={isLoading}
          onClick={() => void runPreview()}
          data-testid={suffixTestId('run', props)}
        />
      </HStack>
      <Show when={hasOutdatedResult}>
        <Alert
          variant="info"
          message={i18n.t('entity.promoPhotos.preview.labels.outdated')}
          data-testid={suffixTestId('outdated', props)}
        />
      </Show>
      <Show when={isCurrent ? error : null}>
        {(previewError) => (
          <Alert
            variant="error"
            message={getApiErrorMessages(previewError)}
            data-testid={suffixTestId('error', props)}
          />
        )}
      </Show>
      <Show when={isCurrent ? preview : null}>
        {(result) => (
          <VehicleSelectionPreviewResult
            preview={result}
            data-testid={suffixTestId('result', props)}
          />
        )}
      </Show>
    </VStack>
  );
}
